import getUserLocale from 'get-user-locale';
import { getEndpoint, ENDPOINTS } from '@busuu/endpoints';
import cloneDeep from 'lodash/cloneDeep';
import { EN_CODE } from '@busuu/languages';
import axios, { AxiosError } from 'axios';
import { getParameter } from '@busuu/query-params';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import { isEligibleForReferralFreeTrial } from '@busuu/user';

// Pricing filters
var DELETED_INTERVAL_LENGTHS = [6];
var ONE_MONTH_PLAN_COUNTRIES = ['US', 'RU', 'BR', 'TR'];
// Required checkout parameters
var CHECKOUT_PARAMS = {
    PRICE_ID: 'price_id',
    COHORT: 'cohort',
};
/**
 * Different types of FT
 * These vary according to duration
 * TODO: these should map to PRICING_COHORTS to avoid having multiple strings for the same thing
 */
var FREE_TRIAL_TYPES = {
    /** Basic FT, 7 days */
    BASIC: 'free-trial-basic',
    /** Referral FT, 30 days */
    REFERRAL: 'free-trial-referral',
};
/**
 * When 'selling' a free trial, we specifically pick the following plan:
 * - premium_plus
 * - duration of 12 months
 */
var FREE_TRIAL_PLAN = {
    MONTHS: 12,
    TIER: 'premium_plus',
    EXPIRY_SECONDS: 86400,
};
/**
 * Different user cohorts, we use these when calling
 * 'pricing' endpoints to retrieve a different group of plans
 */
var PRICING_COHORTS = {
    /** Regular pricing */
    STANDARD: 'sub_standard',
    /** Regular Free trial */
    FREE_TRIAL_BASIC: 'free_users',
    /** Referral Free trial */
    FREE_TRIAL_REFERRAL: 'free-trial-web-v1',
    /** Partner: Xing */
    PARTNER_XING: 'ft_partnership_xing',
    /** Partner: Windtre */
    PARTNER_WINDTRE: 'ft_partnership_windtre',
};
/**
 * Specific and separate list for partners
 * This ensures that we can only call pricing related
 * to these partners and it doesn't mix with our regular FT/pricing offerings
 */
var PARTNER_GROUPS = {
    xing: PRICING_COHORTS.PARTNER_XING,
    windtre: PRICING_COHORTS.PARTNER_WINDTRE,
};
// Default user group (fallback if an incorrect one is passed)
var PRICING_COHORT_DEFAULT = PRICING_COHORTS.STANDARD;
// Default country code to pass with a pricing request
var PRICING_DEFAULT_COUNTRY = 'US';
// Pricing platforms
var PRICING_PLATFORMS = {
    WEB: 'web',
    IOS: 'ios',
    ANDROID: 'android',
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Given a price and a currency - returns a formatted price with its relevant symbol
 * @param {Object} product
 * @returns {String} formatted price
 * @private
 */
var formatPriceWithCurrency = function (_a) {
    var amount = _a.amount, _b = _a.currency, currency = _b === void 0 ? 'GBP' : _b, _c = _a.decimals, decimals = _c === void 0 ? 2 : _c, _d = _a.userLocale, userLocale = _d === void 0 ? 'en-GB' : _d;
    var getPrice = function () {
        if (amount) {
            return decimals ? amount / 100 : Math.ceil(amount);
        }
        return 0;
    };
    return new Intl.NumberFormat(userLocale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimals,
    }).format(getPrice());
};
/**
 * Calculates a monthly price
 * @param {number} amount - amount in cents
 * @param {number} months - number of months
 * @returns {number} monthly price
 */
var getMonthlyPrice = function (amount, months) {
    if (months === void 0) { months = 1; }
    if (months === 0)
        return 0;
    return Math.round(amount / months);
};
/**
 * Removes plans products based on some conditions
 * 1. Remove based on permanently deleted plans
 * 2. Switch 3 month to 1 month plan for ONE_MONTH_PLAN_COUNTRIES
 * @param {Array} productsData
 * @param {String} countryCode
 * @return {Array}
 */
var filterRegularPrices = function (productsData, countryCode) {
    var monthToRemoveByCountry = ONE_MONTH_PLAN_COUNTRIES.includes(countryCode) ? 3 : 1;
    var monthsToRemove = DELETED_INTERVAL_LENGTHS.concat([monthToRemoveByCountry]);
    return productsData.map(function (product) { return (__assign(__assign({}, product), { prices: product.prices.filter(function (price) { return !monthsToRemove.includes(price.interval_count); }) })); });
};
/**
 * Decorates the plans with human readable properties
 * that can be used by the frontend
 * @param {Array} products - list of products
 * @param {String} userLocale - user locale ie. 'es-ES'
 * @returns {Array} products
 */
var decorateProducts = function (products, userLocale) {
    return products.map(function (product) { return (__assign(__assign({}, product), { prices: product.prices.map(function (price) {
            var currency = price.currency, decimals = price.number_of_decimals;
            return __assign(__assign({}, price), { product_id: product.product_id, amount_human_readable: formatPriceWithCurrency({
                    amount: price.amount,
                    currency: currency,
                    decimals: decimals,
                    userLocale: userLocale,
                }), amount_after_discount_human_readable: price.amount_after_discount
                    ? formatPriceWithCurrency({
                        amount: price.amount_after_discount,
                        currency: currency,
                        decimals: decimals,
                        userLocale: userLocale,
                    })
                    : null, amount_saved_human_readable: price.amount_saved
                    ? formatPriceWithCurrency({
                        amount: price.amount_saved,
                        currency: currency,
                        decimals: decimals,
                        userLocale: userLocale,
                    })
                    : null, free_trial_duration_days: price.free_trial_duration
                    ? Math.floor(price.free_trial_duration / 1440)
                    : 0, amount_monthly: getMonthlyPrice(price.amount, price.interval_count), amount_monthly_human_readable: formatPriceWithCurrency({
                    amount: getMonthlyPrice(price.amount, price.interval_count),
                    currency: currency,
                    decimals: decimals,
                    userLocale: userLocale,
                }), amount_monthly_discounted: price.amount_after_discount
                    ? getMonthlyPrice(price.amount_after_discount, price.interval_count)
                    : null, amount_monthly_discounted_human_readable: price.amount_after_discount
                    ? formatPriceWithCurrency({
                        amount: getMonthlyPrice(price.amount_after_discount, price.interval_count),
                        currency: currency,
                        decimals: decimals,
                        userLocale: userLocale,
                    })
                    : null, amount_free_trial_human_readable: price.free_trial_duration
                    ? formatPriceWithCurrency({
                        amount: 0,
                        currency: currency,
                        decimals: decimals,
                        userLocale: userLocale,
                    })
                    : null });
        }) })); });
};
/**
 * Formats the received productsData object to have paywall type included
 * inside each product and returns an array of products
 * @param {Object} productsData - productsData object
 * @returns {Array} - products
 */
var formatProductsResponse = function (productsData) {
    return productsData.products.map(function (product) { return (__assign(__assign({}, product), { paywall: (productsData === null || productsData === void 0 ? void 0 : productsData.paywall) || null })); });
};

// Vars
var FILENAME$1 = 'pricing-service.ts';
/* istanbul ignore next */
var logError$1 = function (message, error) {
    console.error(FILENAME$1 + " - " + message + " - " + JSON.stringify(error, Object.getOwnPropertyNames(error)));
};
// If pricing type is REGULAR - NonDecoratedRegularProductsObject is returned.
/* istanbul ignore next */
var fetchPricing = function (url) { return __awaiter(void 0, void 0, void 0, function () {
    var res;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, axios.get(url, {
                    withCredentials: true,
                })];
            case 1:
                res = _a.sent();
                return [2 /*return*/, res === null || res === void 0 ? void 0 : res.data];
        }
    });
}); };
/**
 * Calls the pricing endpoint and returns all plans
 * @param {String} userGroup - pricing user group
 * @returns {Promise} pricing
 */
var getPricing = function (_a) {
    var _b = _a.user, user = _b === void 0 ? null : _b, countryCode = _a.countryCode, baseUrl = _a.baseUrl, _c = _a.couponCode, couponCode = _c === void 0 ? '' : _c, _d = _a.pricingCohort, pricingCohort = _d === void 0 ? PRICING_COHORT_DEFAULT : _d, _e = _a.filterUnusedPrices, filterUnusedPrices = _e === void 0 ? true : _e, _f = _a.interfaceLang, interfaceLang = _f === void 0 ? EN_CODE : _f, _g = _a.platform, platform = _g === void 0 ? PRICING_PLATFORMS.WEB : _g;
    return __awaiter(void 0, void 0, void 0, function () {
        var url, response, productsReq, prices, userLocale, error_1, errorMsg;
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0:
                    // Check for required params
                    if (!countryCode || !baseUrl) {
                        throw new Error('`countryCode` or `baseUrl` parameters missing');
                    }
                    url = getEndpoint({
                        baseUrl: baseUrl,
                        endpoint: user ? ENDPOINTS.PRICING_AUTH : ENDPOINTS.PRICING,
                        replacements: {
                            '{userGroup}': pricingCohort,
                            '{countryCode}': countryCode,
                            '{interfaceLanguage}': interfaceLang,
                            '{platform}': platform,
                        },
                    });
                    // Get discount coupon if present & attach to end of url
                    if (couponCode) {
                        url += "&coupon_code=" + couponCode;
                    }
                    _h.label = 1;
                case 1:
                    _h.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetchPricing(url)];
                case 2:
                    response = _h.sent();
                    productsReq = formatProductsResponse(response);
                    prices = filterUnusedPrices ? filterRegularPrices(productsReq, countryCode) : cloneDeep(productsReq);
                    userLocale = getUserLocale();
                    return [2 /*return*/, decorateProducts(prices, userLocale)];
                case 3:
                    error_1 = _h.sent();
                    errorMsg = "getPricing() error: " + JSON.stringify(error_1, Object.getOwnPropertyNames(error_1), 4);
                    logError$1(errorMsg, error_1);
                    if (error_1 instanceof AxiosError) {
                        throw error_1;
                    }
                    throw new Error(errorMsg);
                case 4: return [2 /*return*/];
            }
        });
    });
};
var initHTTPClient = function (options) {
    if (options.authorization) {
        axios.defaults.headers.common['Authorization'] = options.authorization;
    }
};

/**
 * Flattens a list of products into an array of plan objects
 * @param {object} products
 * @param {number} lessons
 * @returns {object} plan - ProductItemInterface
 */
var flattenPrices = function (products) {
    return products.reduce(function (acc, _a) {
        var prices = _a.prices;
        return acc.concat(prices);
    }, []);
};
/**
 * Finds a plan by ID
 * @param {object} products
 * @param {number} lessons
 * @returns {object} plan - ProductItemInterface
 */
var findPlanById = function (products, planId) {
    return products.reduce(function (acc, product) { return acc || product.prices.find(function (price) { return price.id === planId; }); }, undefined);
};
/**
 * Finds a plan by tier and duration
 * @param {object} products
 * @param {number} interval
 * @param {string} tier
 * @returns {object} plan - ProductItemInterface
 */
var findPlanByTierAndDuration = function (products, interval, tier) {
    return products.reduce(function (acc, product) {
        return acc ||
            product.prices.find(function (price) {
                return price.interval_count === interval && price.product_id === tier;
            });
    }, undefined);
};
/**
 * Finds Free trial plan
 * These constants are set by the product team
 * @param {object} products - products
 * @returns {object} plan - ProductItemInterface
 */
var findFreeTrialPlan = function (products) {
    return findPlanByTierAndDuration(products, FREE_TRIAL_PLAN.MONTHS, FREE_TRIAL_PLAN.TIER);
};
/**
 * Finds the cheapest available price within a list of products
 * @param {array} products
 * @returns {object} cheapest price plan
 */
var findCheapestAvailablePrice = function (products) {
    // Flatten into an array of all prices
    var allPrices = flattenPrices(products);
    return minBy(allPrices, function (price) {
        if (!price)
            return null;
        return price.discount_percent ? price.amount_after_discount : price.amount;
    });
};
/**
 * Finds the cheapest available price per month within a list of products
 * @param {array} products
 * @returns {object} cheapest price per month plan
 */
var findCheapestAvailablePricePerMonth = function (products) {
    // Flatten into an array of all prices
    var allPrices = flattenPrices(products);
    return minBy(allPrices, function (price) {
        if (!price)
            return null;
        return price.discount_percent ? price.amount_monthly_discounted : price.amount_monthly;
    });
};
/**
 * Finds the highest discount within a list of products
 * @param {array} products
 * @returns {number} price with highest discount
 */
var findHighestAvailableDiscount = function (products) {
    // Flatten into an array of all prices
    var allPrices = flattenPrices(products);
    return maxBy(allPrices, function (price) {
        if (!price)
            return null;
        return price.amount_after_discount || 0;
    });
};
/**
 * Builds the required parameters to send a user to the checkout
 * @param {string} id - price ID
 * @param {string} cohort - pricing cohort
 * @returns {string} url query string
 */
var buildCheckoutRequiredParams = function (id, cohort) {
    var PRICE_ID = CHECKOUT_PARAMS.PRICE_ID, COHORT = CHECKOUT_PARAMS.COHORT;
    return PRICE_ID + "=" + id + "&" + COHORT + "=" + cohort;
};
/**
 * Used in the checkout - returns the required parameters
 * to retrieve a plan from the backend
 * @returns {Object} parameters
 */
var getCheckoutRequiredParams = function () {
    var priceId = getParameter(CHECKOUT_PARAMS.PRICE_ID);
    var cohort = getParameter(CHECKOUT_PARAMS.COHORT);
    return { priceId: priceId, cohort: cohort };
};
/**
 * Finds plans by product id
 * @param {object} products
 * @param {string} productId - product identifier
 * @returns {object} plan - ProductItemInterface
 */
var findPlansByProductId = function (products, productId) {
    var _a;
    return (_a = products === null || products === void 0 ? void 0 : products.find(function (product) { return product.product_id === productId; })) === null || _a === void 0 ? void 0 : _a.prices;
};

// Vars
var FILENAME = 'pricing-free-trial.helpers.ts';
var logError = function (message, error) {
    console.error(FILENAME + " - " + message + " - " + JSON.stringify(error, Object.getOwnPropertyNames(error)));
};
var fetchFreeTrialEligiblity = function (url) { return __awaiter(void 0, void 0, void 0, function () {
    var res;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, axios.get(url, { withCredentials: true })];
            case 1:
                res = _a.sent();
                return [2 /*return*/, res === null || res === void 0 ? void 0 : res.data];
        }
    });
}); };
/**
 * Returns the type of trial the user is eligible for.
 * @return {string?}
 */
var getFreeTrialType = function (baseUrl, user) { return __awaiter(void 0, void 0, void 0, function () {
    var url, isEligible, error_1, errorMsg;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                url = getEndpoint({
                    baseUrl: baseUrl,
                    endpoint: ENDPOINTS.SUBSCRIPTION_FT_ELIGIBILITY,
                    replacements: {
                        '{userUuid}': user.uuid,
                    },
                });
                _a.label = 1;
            case 1:
                _a.trys.push([1, 3, , 4]);
                return [4 /*yield*/, fetchFreeTrialEligiblity(url)];
            case 2:
                isEligible = (_a.sent()).isCustomerEligibleForFreeTrial;
                if (isEligibleForReferralFreeTrial(user, isEligible)) {
                    return [2 /*return*/, FREE_TRIAL_TYPES.REFERRAL];
                }
                if (isEligible) {
                    return [2 /*return*/, FREE_TRIAL_TYPES.BASIC];
                }
                return [2 /*return*/, null];
            case 3:
                error_1 = _a.sent();
                errorMsg = "getFreeTrialType() error: " + JSON.stringify(error_1, Object.getOwnPropertyNames(error_1), 4);
                logError(errorMsg, error_1);
                throw new Error(errorMsg);
            case 4: return [2 /*return*/];
        }
    });
}); };
/**
 * Returns a pricing cohort based on a 'free trial type'
 *
 * TODO: It'd be reaaaally nice to avoid having this and avoid having
 * different strings for 'free trial types' and 'pricing cohorts'.
 * Eventually (phase 2) we'd want a map of FREE_TRIAL_TYPES to match the
 * strings in PRICING_COHORTS.
 *
 * @param {string} trialType
 * @returns {string} pricingCohort
 */
var getFreeTrialPricingCohort = function (trialType) {
    switch (trialType) {
        case FREE_TRIAL_TYPES.REFERRAL:
            return PRICING_COHORTS.FREE_TRIAL_REFERRAL;
        case FREE_TRIAL_TYPES.BASIC:
        default:
            return PRICING_COHORTS.FREE_TRIAL_BASIC;
    }
};

var RAW_PRICE_1_MONTH = {
    amount: 800,
    amount_after_discount: null,
    amount_saved: null,
    apple_id: null,
    apple_discount_id: null,
    currency: 'EUR',
    discount_percent: 0,
    free_trial_duration: 0,
    id: 'price-1-month',
    interval: 'month',
    interval_count: 1,
    number_of_decimals: 2,
    type: 'subscription',
    providers: ['stripe', 'braintree'],
    providers_data: [
        { provider_name: 'stripe', price_id: '123', discount_id: '123' },
        { provider_name: 'braintree', price_id: '123', discount_id: '123' },
    ],
};
var PRICE_1_MONTH = __assign(__assign({}, RAW_PRICE_1_MONTH), { amount_human_readable: '€8.00', amount_after_discount_human_readable: null, amount_saved_human_readable: null, amount_monthly: 800, amount_monthly_human_readable: '€8.00', amount_monthly_discounted: null, amount_monthly_discounted_human_readable: null, product_id: '', free_trial_duration_days: 0, amount_free_trial_human_readable: null });

var RAW_PRICE_3_MONTH = {
    amount: 1200,
    amount_after_discount: null,
    amount_saved: null,
    apple_id: null,
    apple_discount_id: null,
    currency: 'EUR',
    discount_percent: 0,
    free_trial_duration: 0,
    id: 'price-3-month',
    interval: 'month',
    interval_count: 3,
    number_of_decimals: 2,
    type: 'subscription',
    providers: ['stripe', 'braintree'],
    providers_data: [
        { provider_name: 'stripe', price_id: '123', discount_id: '123' },
        { provider_name: 'braintree', price_id: '123', discount_id: '123' },
    ],
};
var PRICE_3_MONTH = __assign(__assign({}, RAW_PRICE_3_MONTH), { amount_human_readable: '€12.00', amount_after_discount_human_readable: null, amount_saved_human_readable: null, amount_monthly: 400, amount_monthly_human_readable: '€4.00', amount_monthly_discounted: null, amount_monthly_discounted_human_readable: null, product_id: '', free_trial_duration_days: 0, amount_free_trial_human_readable: null });

var RAW_PRICE_6_MONTH = {
    amount: 1800,
    amount_after_discount: null,
    amount_saved: null,
    apple_id: null,
    apple_discount_id: null,
    currency: 'EUR',
    discount_percent: 0,
    free_trial_duration: 0,
    id: 'price-6-month',
    interval: 'month',
    interval_count: 6,
    number_of_decimals: 2,
    type: 'subscription',
    providers: ['stripe', 'braintree'],
    providers_data: [
        { provider_name: 'stripe', price_id: '123', discount_id: '123' },
        { provider_name: 'braintree', price_id: '123', discount_id: '123' },
    ],
};
var PRICE_6_MONTH = __assign(__assign({}, RAW_PRICE_6_MONTH), { amount_human_readable: '€18.00', amount_after_discount_human_readable: null, amount_saved_human_readable: null, amount_monthly: 300, amount_monthly_human_readable: '€3.00', amount_monthly_discounted: null, amount_monthly_discounted_human_readable: null, product_id: '', free_trial_duration_days: 0, amount_free_trial_human_readable: null });

var RAW_PRICE_12_MONTH = {
    amount: 2400,
    amount_after_discount: null,
    amount_saved: null,
    apple_id: null,
    apple_discount_id: null,
    currency: 'EUR',
    discount_percent: 0,
    free_trial_duration: 0,
    id: 'price-12-month',
    interval: 'month',
    interval_count: 12,
    number_of_decimals: 2,
    type: 'subscription',
    providers: ['stripe', 'braintree'],
    providers_data: [
        { provider_name: 'stripe', price_id: '123', discount_id: '123' },
        { provider_name: 'braintree', price_id: '123', discount_id: '123' },
    ],
};
var PRICE_12_MONTH = __assign(__assign({}, RAW_PRICE_12_MONTH), { amount_human_readable: '€24.00', amount_after_discount_human_readable: null, amount_saved_human_readable: null, amount_monthly: 200, amount_monthly_human_readable: '€2.00', amount_monthly_discounted: null, amount_monthly_discounted_human_readable: null, product_id: '', free_trial_duration_days: 0, amount_free_trial_human_readable: null });

var RAW_PRICE_12_MONTH_DISCOUNTED = {
    amount: 2400,
    amount_after_discount: 1800,
    amount_saved: 600,
    apple_id: null,
    apple_discount_id: null,
    currency: 'EUR',
    discount_percent: 25,
    free_trial_duration: 0,
    id: 'price-12-month-discounted',
    interval: 'month',
    interval_count: 12,
    number_of_decimals: 2,
    type: 'subscription',
    providers: ['stripe', 'braintree'],
    providers_data: [
        { provider_name: 'stripe', price_id: '123', discount_id: '123' },
        { provider_name: 'braintree', price_id: '123', discount_id: '123' },
    ],
};
var PRICE_12_MONTH_DISCOUNTED = __assign(__assign({}, RAW_PRICE_12_MONTH_DISCOUNTED), { amount_human_readable: '€24.00', amount_after_discount_human_readable: '€18.00', amount_saved_human_readable: '€6.00', amount_monthly: 200, amount_monthly_human_readable: '€2.00', amount_monthly_discounted: 150, amount_monthly_discounted_human_readable: '€1.50', product_id: '', free_trial_duration_days: 0, amount_free_trial_human_readable: null });

// Product IDs
var PRODUCT_ID_STANDARD$2 = 'premium_standard';
var PRODUCT_ID_PLUS$2 = 'premium_plus';
var PAYWALL_TYPE$2 = 'BASIC_PAYWALL';
var MOCK_RAW_PRICING_DISCOUNTED = [
    {
        name: PRODUCT_ID_STANDARD$2,
        product_id: PRODUCT_ID_STANDARD$2,
        prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH],
        paywall: PAYWALL_TYPE$2,
    },
    {
        name: PRODUCT_ID_PLUS$2,
        product_id: PRODUCT_ID_PLUS$2,
        prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH_DISCOUNTED],
        paywall: PAYWALL_TYPE$2,
    },
];
var MOCK_PRICING_DISCOUNTED = [
    {
        name: PRODUCT_ID_STANDARD$2,
        product_id: PRODUCT_ID_STANDARD$2,
        prices: [
            __assign(__assign({}, PRICE_1_MONTH), { product_id: PRODUCT_ID_STANDARD$2 }),
            __assign(__assign({}, PRICE_3_MONTH), { product_id: PRODUCT_ID_STANDARD$2 }),
            __assign(__assign({}, PRICE_6_MONTH), { product_id: PRODUCT_ID_STANDARD$2 }),
            __assign(__assign({}, PRICE_12_MONTH), { product_id: PRODUCT_ID_STANDARD$2 }),
        ],
        paywall: PAYWALL_TYPE$2,
    },
    {
        name: PRODUCT_ID_PLUS$2,
        product_id: PRODUCT_ID_PLUS$2,
        prices: [
            __assign(__assign({}, PRICE_1_MONTH), { product_id: PRODUCT_ID_PLUS$2 }),
            __assign(__assign({}, PRICE_3_MONTH), { product_id: PRODUCT_ID_PLUS$2 }),
            __assign(__assign({}, PRICE_6_MONTH), { product_id: PRODUCT_ID_PLUS$2 }),
            __assign(__assign({}, PRICE_12_MONTH_DISCOUNTED), { product_id: PRODUCT_ID_PLUS$2 }),
        ],
        paywall: PAYWALL_TYPE$2,
    },
];

var RAW_PRICE_12_MONTH_FREE_TRIAL = {
    amount: 2400,
    amount_after_discount: null,
    amount_saved: null,
    apple_id: null,
    apple_discount_id: null,
    currency: 'EUR',
    discount_percent: 0,
    free_trial_duration: 43200,
    id: 'price-12-month-free-trial',
    interval: 'month',
    interval_count: 12,
    number_of_decimals: 2,
    type: 'subscription',
    providers: ['stripe', 'braintree'],
    providers_data: [
        { provider_name: 'stripe', price_id: '123', discount_id: '123' },
        { provider_name: 'braintree', price_id: '123', discount_id: '123' },
    ],
};
var PRICE_12_MONTH_FREE_TRIAL = __assign(__assign({}, RAW_PRICE_12_MONTH_FREE_TRIAL), { amount_human_readable: '€24.00', amount_after_discount_human_readable: null, amount_saved_human_readable: null, amount_monthly: 200, amount_monthly_human_readable: '€2.00', amount_monthly_discounted: null, amount_monthly_discounted_human_readable: null, product_id: '', free_trial_duration_days: 30, amount_free_trial_human_readable: '€0.00' });

// Product IDs
var PRODUCT_ID_STANDARD$1 = 'premium_standard';
var PRODUCT_ID_PLUS$1 = 'premium_plus';
var PAYWALL_TYPE$1 = 'BASIC_PAYWALL';
var MOCK_RAW_PRICING_FREE_TRIAL = [
    {
        name: PRODUCT_ID_STANDARD$1,
        product_id: PRODUCT_ID_STANDARD$1,
        prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH_FREE_TRIAL],
        paywall: PAYWALL_TYPE$1,
    },
    {
        name: PRODUCT_ID_PLUS$1,
        product_id: PRODUCT_ID_PLUS$1,
        prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH_FREE_TRIAL],
        paywall: PAYWALL_TYPE$1,
    },
];
var MOCK_PRICING_FREE_TRIAL = [
    {
        name: PRODUCT_ID_STANDARD$1,
        product_id: PRODUCT_ID_STANDARD$1,
        prices: [
            __assign(__assign({}, PRICE_1_MONTH), { product_id: PRODUCT_ID_STANDARD$1 }),
            __assign(__assign({}, PRICE_3_MONTH), { product_id: PRODUCT_ID_STANDARD$1 }),
            __assign(__assign({}, PRICE_6_MONTH), { product_id: PRODUCT_ID_STANDARD$1 }),
            __assign(__assign({}, PRICE_12_MONTH_FREE_TRIAL), { product_id: PRODUCT_ID_STANDARD$1 }),
        ],
        paywall: PAYWALL_TYPE$1,
    },
    {
        name: PRODUCT_ID_PLUS$1,
        product_id: PRODUCT_ID_PLUS$1,
        prices: [
            __assign(__assign({}, PRICE_1_MONTH), { product_id: PRODUCT_ID_PLUS$1 }),
            __assign(__assign({}, PRICE_3_MONTH), { product_id: PRODUCT_ID_PLUS$1 }),
            __assign(__assign({}, PRICE_6_MONTH), { product_id: PRODUCT_ID_PLUS$1 }),
            __assign(__assign({}, PRICE_12_MONTH_FREE_TRIAL), { product_id: PRODUCT_ID_PLUS$1 }),
        ],
        paywall: PAYWALL_TYPE$1,
    },
];

// Product IDs
var PRODUCT_ID_STANDARD = 'premium_standard';
var PRODUCT_ID_PLUS = 'premium_plus';
var PAYWALL_TYPE = 'BASIC_PAYWALL';
var MOCK_RAW_PRICING_REGULAR_ENDPOINT_RESPONSE = {
    paywall: PAYWALL_TYPE,
    products: [
        {
            name: PRODUCT_ID_STANDARD,
            product_id: PRODUCT_ID_STANDARD,
            prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH],
        },
        {
            name: PRODUCT_ID_PLUS,
            product_id: PRODUCT_ID_PLUS,
            prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH],
        },
    ],
};
var MOCK_RAW_PRICING_REGULAR = [
    {
        name: PRODUCT_ID_STANDARD,
        product_id: PRODUCT_ID_STANDARD,
        prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH],
        paywall: PAYWALL_TYPE,
    },
    {
        name: PRODUCT_ID_PLUS,
        product_id: PRODUCT_ID_PLUS,
        prices: [RAW_PRICE_1_MONTH, RAW_PRICE_3_MONTH, RAW_PRICE_6_MONTH, RAW_PRICE_12_MONTH],
        paywall: PAYWALL_TYPE,
    },
];
var MOCK_PRICING_REGULAR = [
    {
        name: PRODUCT_ID_STANDARD,
        product_id: PRODUCT_ID_STANDARD,
        prices: [
            __assign(__assign({}, PRICE_1_MONTH), { product_id: PRODUCT_ID_STANDARD }),
            __assign(__assign({}, PRICE_3_MONTH), { product_id: PRODUCT_ID_STANDARD }),
            __assign(__assign({}, PRICE_6_MONTH), { product_id: PRODUCT_ID_STANDARD }),
            __assign(__assign({}, PRICE_12_MONTH), { product_id: PRODUCT_ID_STANDARD }),
        ],
        paywall: PAYWALL_TYPE,
    },
    {
        name: PRODUCT_ID_PLUS,
        product_id: PRODUCT_ID_PLUS,
        prices: [
            __assign(__assign({}, PRICE_1_MONTH), { product_id: PRODUCT_ID_PLUS }),
            __assign(__assign({}, PRICE_3_MONTH), { product_id: PRODUCT_ID_PLUS }),
            __assign(__assign({}, PRICE_6_MONTH), { product_id: PRODUCT_ID_PLUS }),
            __assign(__assign({}, PRICE_12_MONTH), { product_id: PRODUCT_ID_PLUS }),
        ],
        paywall: PAYWALL_TYPE,
    },
];

export { CHECKOUT_PARAMS, DELETED_INTERVAL_LENGTHS, FREE_TRIAL_PLAN, FREE_TRIAL_TYPES, MOCK_PRICING_DISCOUNTED, MOCK_PRICING_FREE_TRIAL, MOCK_PRICING_REGULAR, MOCK_RAW_PRICING_DISCOUNTED, MOCK_RAW_PRICING_FREE_TRIAL, MOCK_RAW_PRICING_REGULAR, MOCK_RAW_PRICING_REGULAR_ENDPOINT_RESPONSE, ONE_MONTH_PLAN_COUNTRIES, PARTNER_GROUPS, PRICING_COHORTS, PRICING_COHORT_DEFAULT, PRICING_DEFAULT_COUNTRY, PRICING_PLATFORMS, buildCheckoutRequiredParams, fetchFreeTrialEligiblity, fetchPricing, findCheapestAvailablePrice, findCheapestAvailablePricePerMonth, findFreeTrialPlan, findHighestAvailableDiscount, findPlanById, findPlanByTierAndDuration, findPlansByProductId, flattenPrices, formatPriceWithCurrency, getCheckoutRequiredParams, getFreeTrialPricingCohort, getFreeTrialType, getPricing, initHTTPClient };
//# sourceMappingURL=index.esm.js.map
